import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { Table, Card, Typography, Button, Space, Tag, Spin, Input, Row, Col, message, Modal, Form } from 'antd'
import { ReloadOutlined, SearchOutlined, PlusOutlined, EditOutlined, DeleteOutlined, ExclamationCircleOutlined } from '@ant-design/icons'
import { StyledPage, StyledCard } from './ArcCategories.styles'
import useAction from 'utils/useAction'
import * as arcCategoriesActions from 'actions/views/arcCategories'

const { Title, Text } = Typography
const { Search } = Input
const { confirm } = Modal

const ArcCategoriesComponent = ({ categories, isLoaded, isSaving, onRefresh }) => {
  const [searchText, setSearchText] = useState('')
  const [isCreateModalVisible, setIsCreateModalVisible] = useState(false)
  const [isEditModalVisible, setIsEditModalVisible] = useState(false)
  const [editingCategory, setEditingCategory] = useState(null)
  const [createForm] = Form.useForm()
  const [editForm] = Form.useForm()

  const createCategory = useAction(arcCategoriesActions.createArcCategory)
  const updateCategory = useAction(arcCategoriesActions.updateArcCategory)
  const deleteCategory = useAction(arcCategoriesActions.deleteArcCategory)

  const filteredCategories = categories.filter(category =>
    category.attributes.name.toLowerCase().includes(searchText.toLowerCase())
  )

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
      sorter: (a, b) => a.id.localeCompare(b.id),
    },
    {
      title: 'Name',
      dataIndex: ['attributes', 'name'],
      key: 'name',
      sorter: (a, b) => a.attributes.name.localeCompare(b.attributes.name),
      render: (name) => <Text strong>{name}</Text>,
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      render: (_, record) => (
        <Space size="small">
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            Edit
          </Button>
          <Button
            type="link"
            size="small"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record)}
          >
            Delete
          </Button>
        </Space>
      ),
    },
  ]

  const handleEdit = (category) => {
    setEditingCategory(category)
    editForm.setFieldsValue({
      name: category.attributes.name
    })
    setIsEditModalVisible(true)
  }

  const handleDelete = (category) => {
    confirm({
      title: 'Delete Category',
      icon: <ExclamationCircleOutlined />,
      content: `Are you sure you want to delete "${category.attributes.name}"? This action cannot be undone.`,
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk() {
        deleteCategory({
          id: category.id,
          callback: () => {
            onRefresh()
          }
        })
      },
    })
  }

  const handleCreate = () => {
    setIsCreateModalVisible(true)
  }

  const handleCreateSubmit = (values) => {
    createCategory({
      data: {
        type: 'arc_categories',
        attributes: {
          name: values.name
        }
      },
      callback: () => {
        setIsCreateModalVisible(false)
        createForm.resetFields()
        onRefresh()
      }
    })
  }

  const handleEditSubmit = (values) => {
    updateCategory({
      id: editingCategory.id,
      data: {
        type: 'arc_categories',
        id: editingCategory.id,
        attributes: {
          name: values.name
        }
      },
      callback: () => {
        setIsEditModalVisible(false)
        editForm.resetFields()
        setEditingCategory(null)
        onRefresh()
      }
    })
  }

  const handleCreateCancel = () => {
    setIsCreateModalVisible(false)
    createForm.resetFields()
  }

  const handleEditCancel = () => {
    setIsEditModalVisible(false)
    editForm.resetFields()
    setEditingCategory(null)
  }

  return (
    <StyledPage>
      <StyledCard>
        <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
          <Col>
            <Title level={2} style={{ margin: 0 }}>
              Arc Categories
            </Title>
            <Text type="secondary">
              Manage categories for Arc advertising campaigns
            </Text>
          </Col>
          <Col>
            <Space>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleCreate}
              >
                Create Category
              </Button>
            </Space>
          </Col>
        </Row>

        <Row style={{ marginBottom: 16 }}>
          <Col span={8}>
            <Search
              placeholder="Search categories..."
              allowClear
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              prefix={<SearchOutlined />}
            />
          </Col>
        </Row>

        <Spin spinning={!isLoaded || isSaving}>
          <Table
            columns={columns}
            dataSource={filteredCategories}
            rowKey="id"
            pagination={{
              pageSize: 20,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} of ${total} categories`,
            }}
            scroll={{ x: 800 }}
            size="small"
          />
        </Spin>

        {/* Create Modal */}
        <Modal
          title="Create Category"
          open={isCreateModalVisible}
          onCancel={handleCreateCancel}
          footer={null}
          destroyOnClose
        >
          <Form
            form={createForm}
            layout="vertical"
            onFinish={handleCreateSubmit}
          >
            <Form.Item
              name="name"
              label="Category Name"
              rules={[
                { required: true, message: 'Please enter a category name' },
                { min: 1, message: 'Category name cannot be empty' }
              ]}
            >
              <Input placeholder="Enter category name" />
            </Form.Item>
            <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
              <Space>
                <Button onClick={handleCreateCancel}>
                  Cancel
                </Button>
                <Button type="primary" htmlType="submit" loading={isSaving}>
                  Create
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Modal>

        {/* Edit Modal */}
        <Modal
          title="Edit Category"
          open={isEditModalVisible}
          onCancel={handleEditCancel}
          footer={null}
          destroyOnClose
        >
          <Form
            form={editForm}
            layout="vertical"
            onFinish={handleEditSubmit}
          >
            <Form.Item
              name="name"
              label="Category Name"
              rules={[
                { required: true, message: 'Please enter a category name' },
                { min: 1, message: 'Category name cannot be empty' }
              ]}
            >
              <Input placeholder="Enter category name" />
            </Form.Item>
            <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
              <Space>
                <Button onClick={handleEditCancel}>
                  Cancel
                </Button>
                <Button type="primary" htmlType="submit" loading={isSaving}>
                  Update
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Modal>
      </StyledCard>
    </StyledPage>
  )
}

ArcCategoriesComponent.propTypes = {
  categories: PropTypes.array.isRequired,
  isLoaded: PropTypes.bool.isRequired,
  isSaving: PropTypes.bool.isRequired,
  onRefresh: PropTypes.func.isRequired,
}

export default ArcCategoriesComponent